import { useElementSize, useMergedRef } from '@mantine/hooks';
import { useEffect, useRef, useState } from 'react';

import type { BezelProps } from './Bezel.tsx';
import {
  bandPath,
  type Corner,
  type CornerQuad,
  cornerBracketPath,
  cornerSpecs,
  framePath,
  resolveQuad,
  strokePath,
} from './geometry.ts';

type GeometryInput = Pick<
  BezelProps,
  'bevel' | 'radius' | 'strokeWidth' | 'cornerThickness' | 'cornerLength' | 'outerGlowBand'
>;

/**
 * Measures the element and resolves its frame + corner clip paths.
 *
 * Sizing comes from a ResizeObserver (Mantine's useElementSize). Each geometry
 * value resolves prop override → theme CSS var (the element's computed
 * @property value) → registered default; getPropertyValue is only read when the
 * prop is absent. Paths are null until the first measure.
 */
export function useBezelGeometry(input: GeometryInput) {
  const { ref: sizeRef, width, height } = useElementSize<HTMLDivElement>();
  const elementRef = useRef<HTMLDivElement>(null);
  // useElementSize gives a callback ref; merge our own so we can read computed
  // @property values off the live element for the theme-var cascade.
  const ref = useMergedRef<HTMLDivElement>(sizeRef, elementRef);
  const [styles, setStyles] = useState<CSSStyleDeclaration | null>(null);

  useEffect(() => {
    if (!elementRef.current) {
      return;
    }
    setStyles(getComputedStyle(elementRef.current));
  }, []);

  const num = (prop: number | undefined, name: string, fallback: number): number => {
    if (prop !== undefined) {
      return prop;
    }
    if (!styles) {
      return fallback;
    }
    return Number.parseFloat(styles.getPropertyValue(name)) || fallback;
  };

  const quad = (prop: CornerQuad | undefined, name: string, fallback: number): [number, number, number, number] => {
    if (prop !== undefined) {
      return resolveQuad(prop);
    }
    if (!styles) {
      return [fallback, fallback, fallback, fallback];
    }
    const [a, b, c, d] = styles
      .getPropertyValue(name)
      .trim()
      .split(/\s+/)
      .map((part) => Number.parseFloat(part))
      .filter((n) => !Number.isNaN(n));
    if (a === undefined) {
      return [fallback, fallback, fallback, fallback];
    }
    return [a, b ?? a, c ?? a, d ?? b ?? a];
  };

  // Glow band: a "<width> <spread>" length pair (prop string or theme var) →
  // [thickness, outward offset] px for bandPath.
  const band = (prop: string | undefined, name: string, fallback: [number, number]): [number, number] => {
    const raw = (prop ?? (styles ? styles.getPropertyValue(name) : '')).trim();
    const [w, s] = raw.split(/\s+/).map((part) => Number.parseFloat(part));
    if (w === undefined || Number.isNaN(w)) {
      return fallback;
    }
    return [w, s === undefined || Number.isNaN(s) ? fallback[1] : s];
  };

  const specs = cornerSpecs(quad(input.bevel, '--bezel-bevel', 12), quad(input.radius, '--bezel-radius', 0));
  const strokeWidth = num(input.strokeWidth, '--bezel-stroke-width', 1);
  const thickness = num(input.cornerThickness, '--bezel-corner-thickness', 4);
  const length = num(input.cornerLength, '--bezel-corner-length', 40);
  const [glowWidth, glowSpread] = band(input.outerGlowBand, '--bezel-outer-glow-band', [10, 0]);

  const frame = framePath(width, height, specs);
  const bracket = (corner: Corner) =>
    `path('${cornerBracketPath(corner, width, height, specs[corner], thickness, length)}')`;

  if (width <= 0 || height <= 0) {
    return { ref, width, height, paths: null };
  }

  return {
    ref,
    width,
    height,
    paths: {
      clip: `path('${frame}')`,
      stroke: `path(evenodd, '${strokePath(width, height, specs, strokeWidth)}')`,
      corners: { tl: bracket('tl'), tr: bracket('tr'), br: bracket('br'), bl: bracket('bl') },
      outerGlow: `path(evenodd, '${bandPath(width, height, specs, glowWidth, glowSpread)}')`,
    },
  };
}
